import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const QUICK_CHIPS = [
  { id: "which-toolkit", label: "Which toolkit do I need?" },
  { id: "da-vs-cdc", label: "What's the difference between a DA and CDC?" },
  { id: "book-planner", label: "Will planning consultations be available?" },
];

const getSessionId = () => {
  let id = localStorage.getItem("pfn_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("pfn_chat_session", id);
  }
  return id;
};

const LeadForm = ({ sessionId, onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/chat/lead", { session_id: sessionId, ...form });
      toast.success(`Thanks ${form.name.split(" ")[0]} — we'll be in touch. Reference ${data.enquiry_ref}`);
      onDone(`I'd like to be contacted — my name is ${form.name}.`);
    } catch {
      toast.error("Could not send your details. Please try the contact page.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form onSubmit={submit} className="border-t border-cloud p-4 space-y-2.5 bg-sand-light/50" data-testid="chat-lead-form">
      <p className="text-xs font-mono uppercase tracking-[0.14em] text-teal">Leave your details</p>
      <input
        required
        value={form.name}
        onChange={set("name")}
        placeholder="Your name"
        data-testid="chat-lead-name"
        className="w-full bg-white border border-cloud px-3 py-2.5 text-sm focus:outline-none focus:border-teal"
      />
      <input
        required
        type="email"
        value={form.email}
        onChange={set("email")}
        placeholder="Your email"
        data-testid="chat-lead-email"
        className="w-full bg-white border border-cloud px-3 py-2.5 text-sm focus:outline-none focus:border-teal"
      />
      <textarea
        required
        value={form.message}
        onChange={set("message")}
        placeholder="What would you like help with?"
        rows={2}
        data-testid="chat-lead-message"
        className="w-full bg-white border border-cloud px-3 py-2.5 text-sm focus:outline-none focus:border-teal resize-none"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="chat-lead-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-teal text-ivory text-sm font-medium px-4 py-2.5 hover:bg-teal-dark transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        Send my details
      </button>
    </form>
  );
};

const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open || messages.length) return;
    api
      .get(`/chat/history/${sessionId.current}`)
      .then(({ data }) => setMessages(data.messages.map((m) => ({ role: m.role, text: m.text }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  if (pathname.startsWith("/library")) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setShowLead(false);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      if (!res.ok) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let failed = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const payload = ev.slice(6);
          if (payload === "[DONE]") continue;
          const obj = JSON.parse(payload);
          if (obj.t) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + obj.t };
              return copy;
            });
          }
          if (obj.error) failed = obj.error;
        }
      }
      if (failed) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: failed };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry — I couldn't respond just now. Please try again, or reach us via the contact page.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-open-button"
        aria-label={open ? "Close planning assistant" : "Open planning assistant"}
        className="fixed bottom-5 right-5 z-[70] w-14 h-14 rounded-full bg-teal text-ivory shadow-xl flex items-center justify-center hover:bg-teal-dark hover:scale-105 transition-all duration-300"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-[5.5rem] right-5 z-[70] w-[calc(100vw-2.5rem)] max-w-sm bg-white border border-cloud shadow-2xl flex flex-col overflow-hidden"
          style={{ height: "min(34rem, calc(100vh - 8rem))" }}
          data-testid="chat-panel"
        >
          <div className="bg-teal text-ivory px-5 py-4">
            <p className="font-serif text-lg leading-tight">PLAN FIRST Assistant</p>
            <p className="text-xs text-ivory/70 mt-0.5">Ask anything about NSW planning — in plain English.</p>
            <p className="text-[10px] text-ivory/50 mt-1.5" data-testid="chat-ai-disclosure">
              AI-assisted planning education — not professional advice.
            </p>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-ivory" data-testid="chat-message-list">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="bg-white border border-cloud px-4 py-3 text-sm text-charcoal/85 leading-relaxed">
                  Hello! I can help you understand NSW planning — DAs, complying development,
                  zoning, certificates, bushfire, pools and more. What are you planning?
                </div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_CHIPS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => send(c.label)}
                      data-testid={`chat-quick-chip-${c.id}`}
                      className="text-xs text-teal border border-cloud bg-white px-3 py-2 rounded-full hover:border-teal transition-colors"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap max-w-[88%] ${
                  m.role === "user"
                    ? "ml-auto bg-teal text-ivory"
                    : "bg-white border border-cloud text-charcoal/85"
                }`}
              >
                {m.text ||
                  (m.role === "assistant" && streaming && i === messages.length - 1 ? (
                    <Loader2 className="w-4 h-4 animate-spin text-teal" />
                  ) : (
                    ""
                  ))}
              </div>
            ))}
          </div>

          {showLead && <LeadForm sessionId={sessionId.current} onDone={(t) => { setShowLead(false); send(t); }} />}

          <div className="border-t border-cloud bg-white">
            <div className="flex items-center gap-2 px-3 py-2.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your planning question…"
                data-testid="chat-input"
                className="flex-1 text-sm px-3 py-2.5 bg-ivory border border-cloud focus:outline-none focus:border-teal"
              />
              <button
                onClick={() => setShowLead((s) => !s)}
                data-testid="chat-lead-toggle"
                aria-label="Leave your details"
                title="Leave your details"
                className={`p-2.5 border transition-colors ${showLead ? "border-teal text-teal bg-sand-light" : "border-cloud text-charcoal/60 hover:border-teal hover:text-teal"}`}
              >
                <Mail className="w-4 h-4" />
              </button>
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="p-2.5 bg-teal text-ivory hover:bg-teal-dark transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="px-4 pb-2.5 text-[10px] text-charcoal/45 leading-snug">
              General educational information only — not site-specific planning, legal or financial
              advice.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
